import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ContentImageField from "../shared/ContentImageField";
import {
  EditorSection,
  EditorSubsection,
  EditorFormNav,
  EditorListItem,
  EditorListHeader,
} from "../shared/EditorFormLayout";

export { EditorSection, EditorSubsection, EditorFormNav, EditorListItem, EditorListHeader };

const HELP_CHANNEL_TYPES = [
  { value: "sac", label: "SAC (telefone 0800)" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "pcd", label: "Atendimento PcD auditivo" },
  { value: "email", label: "E-mail" },
  { value: "phone", label: "Telefone" },
  { value: "contato", label: "Página de contato" },
  { value: "faq", label: "Perguntas frequentes" },
];

export function StringListEditor({ label, items, onChange, placeholder, helperText }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onChange([...list, ""])}>
          Adicionar
        </Button>
      </Box>
      {helperText ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {helperText}
        </Typography>
      ) : null}
      {list.map((item, i) => (
        <Box key={i} sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={item}
            placeholder={placeholder}
            onChange={(e) => {
              const next = [...list];
              next[i] = e.target.value;
              onChange(next);
            }}
          />
          <IconButton size="small" aria-label="Remover" onClick={() => onChange(list.filter((_, j) => j !== i))}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

export const emptyLink = () => ({ label: "", href: "", external: false });

export function LinkListEditor({ label, items, onChange, showExternal = false }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onChange([...list, emptyLink()])}>
          Adicionar link
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Escreva o texto do botão e para onde ele leva. Ex.: &quot;Comprar passagem&quot; → /passagens
      </Typography>
      {list.map((item, i) => (
        <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            label="Texto do botão"
            value={item.label || ""}
            onChange={(e) => {
              const next = [...list];
              next[i] = { ...next[i], label: e.target.value };
              onChange(next);
            }}
            sx={{ flex: 1, minWidth: 140 }}
          />
          <TextField
            size="small"
            label="Para onde leva"
            value={item.href || ""}
            placeholder="/pagina ou https://..."
            onChange={(e) => {
              const next = [...list];
              next[i] = { ...next[i], href: e.target.value };
              onChange(next);
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />
          {showExternal ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!item.external}
                  onChange={(e) => {
                    const next = [...list];
                    next[i] = { ...next[i], external: e.target.checked };
                    onChange(next);
                  }}
                />
              }
              label="Nova aba"
            />
          ) : null}
          <IconButton size="small" aria-label="Remover" onClick={() => onChange(list.filter((_, j) => j !== i))}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

export function ImageFieldsEditor({
  label,
  image,
  imageAlt,
  onChange,
  variant = "content",
  uploadSubpath = "contents/institutional",
  showAlt = true,
}) {
  return (
    <ContentImageField
      label={label}
      image={image}
      imageAlt={imageAlt}
      onChange={onChange}
      variant={variant}
      uploadSubpath={uploadSubpath}
      showAlt={showAlt}
    />
  );
}

export function BlocksEditor({ blocks, onChange }) {
  const list = Array.isArray(blocks) ? blocks : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Blocos de conteúdo (título + lista)</Typography>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => onChange([...list, { title: "", items: [] }])}
        >
          Adicionar bloco
        </Button>
      </Box>
      {list.map((block, bi) => (
        <Paper key={bi} variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: "grey.50" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="caption" fontWeight={700}>
              Bloco {bi + 1}
            </Typography>
            <IconButton size="small" onClick={() => onChange(list.filter((_, i) => i !== bi))}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Título do bloco"
            value={block.title || ""}
            onChange={(e) => {
              const next = [...list];
              next[bi] = { ...next[bi], title: e.target.value };
              onChange(next);
            }}
            sx={{ mb: 1 }}
          />
          <StringListEditor
            label="Itens da lista"
            items={block.items}
            onChange={(items) => {
              const next = [...list];
              next[bi] = { ...next[bi], items };
              onChange(next);
            }}
            placeholder="Ex.: Documento com foto"
          />
        </Paper>
      ))}
    </Box>
  );
}

export function MetricsEditor({ metrics, onChange }) {
  const list = Array.isArray(metrics) ? metrics : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Medidas e limites</Typography>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => onChange([...list, { label: "", primary: "", secondary: "" }])}
        >
          Adicionar
        </Button>
      </Box>
      {list.map((m, i) => (
        <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
          <TextField size="small" label="Nome" value={m.label || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], label: e.target.value }; onChange(next);
          }} sx={{ flex: 1, minWidth: 120 }} />
          <TextField size="small" label="Valor principal" value={m.primary || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], primary: e.target.value }; onChange(next);
          }} sx={{ minWidth: 100 }} />
          <TextField size="small" label="Detalhe" value={m.secondary || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], secondary: e.target.value }; onChange(next);
          }} sx={{ flex: 1, minWidth: 140 }} />
          <IconButton size="small" onClick={() => onChange(list.filter((_, j) => j !== i))}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

export function ChecksEditor({ checks, onChange }) {
  const list = Array.isArray(checks) ? checks : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Destaques de conferência</Typography>
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onChange([...list, { title: "", body: "" }])}>
          Adicionar
        </Button>
      </Box>
      {list.map((c, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton size="small" onClick={() => onChange(list.filter((_, j) => j !== i))}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <TextField fullWidth size="small" label="Título" value={c.title || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], title: e.target.value }; onChange(next);
          }} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" multiline minRows={2} label="Texto" value={c.body || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], body: e.target.value }; onChange(next);
          }} />
        </Paper>
      ))}
    </Box>
  );
}

export function HelpChannelsEditor({ channels, onChange }) {
  const list = Array.isArray(channels) ? channels : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Canais de atendimento</Typography>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => onChange([...list, { type: "phone", label: "", value: "", href: "" }])}
        >
          Adicionar canal
        </Button>
      </Box>
      {list.map((ch, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton size="small" onClick={() => onChange(list.filter((_, j) => j !== i))}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Tipo de canal</InputLabel>
              <Select
                label="Tipo de canal"
                value={ch.type || "sac"}
                onChange={(e) => {
                  const next = [...list];
                  next[i] = { ...next[i], type: e.target.value };
                  onChange(next);
                }}
              >
                {HELP_CHANNEL_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" label="Nome exibido" value={ch.label || ""} onChange={(e) => {
              const next = [...list]; next[i] = { ...next[i], label: e.target.value }; onChange(next);
            }} sx={{ flex: 1, minWidth: 120 }} />
            <TextField size="small" label="Texto de apoio" value={ch.value || ""} onChange={(e) => {
              const next = [...list]; next[i] = { ...next[i], value: e.target.value }; onChange(next);
            }} sx={{ flex: 1, minWidth: 120 }} />
            <TextField size="small" label="Link (telefone, e-mail ou página)" value={ch.href || ""} onChange={(e) => {
              const next = [...list]; next[i] = { ...next[i], href: e.target.value }; onChange(next);
            }} sx={{ flex: 1, minWidth: 140 }} />
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

export function LabelsEditor({ labels, onChange }) {
  const l = labels && typeof labels === "object" ? labels : {};
  const set = (patch) => onChange({ ...l, ...patch });
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Textos dos botões da página
      </Typography>
      {[
        ["cardDetailLink", "Link Ver detalhes nos cards"],
        ["sectionHighlightsTitle", "Título Resumo rápido"],
        ["sectionLinksTitle", "Título Links oficiais"],
        ["detailOpen", "Texto Abrir (acordeão)"],
        ["detailClose", "Texto Fechar (acordeão)"],
      ].map(([key, lbl]) => (
        <TextField
          key={key}
          fullWidth
          size="small"
          label={lbl}
          value={l[key] || ""}
          onChange={(e) => set({ [key]: e.target.value })}
          sx={{ mb: 1 }}
        />
      ))}
    </Box>
  );
}

export function ParagraphsEditor({ label, paragraphs, onChange }) {
  const text = Array.isArray(paragraphs) ? paragraphs.join("\n\n") : "";
  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      label={label}
      value={text}
      onChange={(e) => {
        const parts = e.target.value.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
        onChange(parts);
      }}
      helperText="Separe parágrafos com linha em branco"
      sx={{ mb: 2 }}
    />
  );
}
